import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Flame, Calendar } from 'lucide-react';
import { QuotaAccount } from '../types/index.js';
import { EduCard } from './EduCard.js';
import { EduProgressBar } from './EduProgressBar.js';

interface QuotaCardProps {
  quota: QuotaAccount | null;
  planName?: string;
  nextRenewal?: string;
}

export const QuotaCard: React.FC<QuotaCardProps> = ({
  quota,
  planName = 'Plano Pro',
  nextRenewal,
}) => {
  if (!quota) {
    return (
      <EduCard title="Franquia Mensal">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-100 rounded w-1/3"></div>
          <div className="h-8 bg-slate-100 rounded"></div>
        </div>
      </EduCard>
    );
  }

  const totalAllowance = quota.monthlyLimit + (quota.bonus || 0);
  const totalCommitted = quota.consumed + quota.reserved;
  const percentage =
    totalAllowance > 0
      ? Math.min(100, Math.round((totalCommitted / totalAllowance) * 100))
      : 100;

  const isExhausted = quota.available <= 0;
  const isNearLimit90 = percentage >= 90 && !isExhausted;
  const isNearLimit80 = percentage >= 80 && percentage < 90;

  return (
    <EduCard
      title={
        <div className="flex items-center gap-2">
          <Flame className="text-brand-primary" size={20} />
          <span>Franquia Mensal de Videoaulas</span>
        </div>
      }
      subtitle={`Assinatura ativa: ${planName}`}
      footer={
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-txt-muted" />
          <span>
            Renovação do ciclo em:{' '}
            <strong className="text-txt-primary">
              {nextRenewal
                ? new Date(nextRenewal).toLocaleDateString('pt-BR')
                : new Date(quota.periodEnd).toLocaleDateString('pt-BR')}
            </strong>
          </span>
        </div>
      }
    >
      {/* Alert Banners */}
      {isExhausted && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-status-error text-xs">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong className="font-semibold block">Franquia Mensal Esgotada (100%)</strong>
            Você utilizou todas as aulas do ciclo atual. Novas renderizações aguardam a renovação ou acréscimo de bônus.
          </div>
        </div>
      )}

      {isNearLimit90 && (
        <div className="mb-4 p-3.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3 text-status-warning text-xs">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong className="font-semibold block">Aviso: Franquia em 90%</strong>
            Resta apenas 1 ou poucas videoaulas disponíveis neste ciclo de faturamento.
          </div>
        </div>
      )}

      {isNearLimit80 && (
        <div className="mb-4 p-3.5 bg-amber-50/60 border border-amber-200/80 rounded-lg flex items-start gap-3 text-amber-700 text-xs">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong className="font-semibold block">Atenção: Franquia em 80%</strong>
            Você atingiu 80% do limite mensal contratado.
          </div>
        </div>
      )}

      {/* Numerical Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-surface-secondary border border-bdr rounded-lg">
          <span className="text-xs text-txt-secondary block">Limite Contratado</span>
          <span className="text-2xl font-bold text-txt-primary mt-1 block">
            {quota.monthlyLimit}{' '}
            {quota.bonus > 0 && (
              <span className="text-xs font-semibold text-status-success">
                +{quota.bonus} bônus
              </span>
            )}
          </span>
          <span className="text-[11px] text-txt-muted">aulas/mês</span>
        </div>

        <div className="p-3 bg-surface-secondary border border-bdr rounded-lg">
          <span className="text-xs text-txt-secondary block">Consumidas</span>
          <span className="text-2xl font-bold text-txt-primary mt-1 block">
            {quota.consumed}
          </span>
          <span className="text-[11px] text-status-success flex items-center gap-1">
            <CheckCircle size={10} /> Entregues
          </span>
        </div>

        <div className="p-3 bg-surface-secondary border border-bdr rounded-lg">
          <span className="text-xs text-txt-secondary block">Reservadas</span>
          <span className="text-2xl font-bold text-brand-secondary mt-1 block">
            {quota.reserved}
          </span>
          <span className="text-[11px] text-txt-muted">Em processamento</span>
        </div>

        <div className="p-3 bg-brand-light/60 border border-orange-200 rounded-lg">
          <span className="text-xs text-brand-primary font-semibold block">Disponíveis</span>
          <span className="text-2xl font-bold text-brand-primary mt-1 block">
            {quota.available}
          </span>
          <span className="text-[11px] text-brand-primary/80">Saldo restante</span>
        </div>
      </div>

      {/* Utilization Progress Bar */}
      <EduProgressBar
        progress={percentage}
        label="Consumo da Franquia"
        sublabel={`Saldo disponível calculado por: Limite (${quota.monthlyLimit}) + Bônus (${quota.bonus}) - Consumidas (${quota.consumed}) - Reservas (${quota.reserved}) = ${quota.available} aulas.`}
        status={isExhausted ? 'error' : isNearLimit90 ? 'warning' : 'normal'}
      />
    </EduCard>
  );
};
